import { io } from 'socket.io-client';

const getAuthToken = () =>
  localStorage.getItem('authToken') ||
  localStorage.getItem('token') ||
  localStorage.getItem('accessToken') ||
  localStorage.getItem('adminToken');

const getStoredUser = () => {
  try {
    const userData = localStorage.getItem('user') ||
                     localStorage.getItem('rebax_user_data') ||
                     localStorage.getItem('authUser') ||
                     localStorage.getItem('admin_user');
    return userData ? JSON.parse(userData) : null;
  } catch (e) {
    return null;
  }
};

class SocketService {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.listeners = {};

    // Event Listeners (Observer Pattern - matching React Native callService)
    this.chatMessageListeners = [];
    this.chatTypingListeners = [];
    this.chatClaimedListeners = [];
    this.chatUnclaimedListeners = [];
    this.chatEndedListeners = [];
    this.chatMessagesReadListeners = [];

    // Connection callbacks
    this.onConnectedCallbacks = [];

    // Conversation room currently open in the chat window (re-joined on reconnect)
    this.activeConversationId = null;

    // A user message is broadcast to both conversation_<id> and admin_room,
    // so the same message can arrive twice — remember recent IDs to dedupe.
    this.recentMessageIds = [];
  }

  /**
   * Initialize socket connection. Safe to call multiple times — reuses the
   * existing socket instead of opening a new one (the server's `register`
   * handler force-disconnects a user's previous socket, so duplicates would
   * kick each other off).
   */
  connect() {
    if (this.socket) {
      if (!this.socket.connected) this.socket.connect();
      return;
    }

    const authToken = getAuthToken();
    if (!authToken) {
      console.warn('⚠️ Socket: no auth token found, server will reject the connection');
    }

    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';

    this.socket = io(apiBaseUrl, {
      transports: ['websocket', 'polling'],
      timeout: 20000,
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
      // Function form so a refreshed token is picked up on every reconnect
      auth: (cb) => cb({ token: getAuthToken() }),
    });

    // Handlers registered once; socket.io keeps them across reconnects
    this.setupChatListeners();

    // 'connect' fires on the initial connection and on every reconnect
    this.socket.on('connect', () => {
      console.log('✅ Socket connected:', this.socket.id);
      this.isConnected = true;
      this.joinAdminRoom();
      if (this.activeConversationId) {
        this.socket.emit('chat:join', { conversationId: this.activeConversationId });
      }
      this.triggerConnectedCallbacks();
    });

    this.socket.on('disconnect', (reason) => {
      console.log('❌ Socket disconnected:', reason);
      this.isConnected = false;
    });

    this.socket.on('connect_error', (error) => {
      console.error('❌ Socket connection error:', error.message);
      this.isConnected = false;
    });
  }

  /**
   * Join admin_room so messages from builders/agents/agencies are delivered.
   * The backend (notification-service) joins admin_room on `register`, using
   * the role from the JWT. `join:admin` is the admin-service fallback.
   */
  joinAdminRoom() {
    if (!this.socket || !this.isConnected) return;
    const user = getStoredUser();
    this.socket.emit('register', {
      userId: user?.id || user?.user_id,
      userType: user?.role || 'admin',
    });
    this.socket.emit('join:admin');
  }

  /**
   * Join a conversation room so we receive its messages, read receipts and
   * chat:ended events. Leaves the previously joined conversation.
   */
  joinConversation(conversationId) {
    if (!conversationId) return;
    if (this.activeConversationId && this.activeConversationId != conversationId) {
      this.leaveConversation(this.activeConversationId);
    }
    this.activeConversationId = conversationId;
    if (this.socket && this.isConnected) {
      this.socket.emit('chat:join', { conversationId });
    }
  }

  leaveConversation(conversationId) {
    if (!conversationId) return;
    if (this.socket && this.isConnected) {
      this.socket.emit('chat:leave', { conversationId });
    }
    if (this.activeConversationId == conversationId) {
      this.activeConversationId = null;
    }
  }

  isDuplicateMessage(msg) {
    if (!msg || msg.id == null) return false;
    const id = String(msg.id);
    if (this.recentMessageIds.includes(id)) return true;
    this.recentMessageIds.push(id);
    if (this.recentMessageIds.length > 200) this.recentMessageIds.shift();
    return false;
  }

  /**
   * Set up chat-specific event listeners - matches React Native callService pattern
   */
  setupChatListeners() {
    if (!this.socket) return;

    const handleIncoming = (data) => {
      if (this.isDuplicateMessage(data)) return;
      this.notify(this.chatMessageListeners, data);
    };

    this.socket.on('chat:receive_message', handleIncoming);

    // Legacy event name, kept for backward compatibility
    this.socket.on('new_message', (data) => {
      handleIncoming(data);
      this.emitToListeners('new_message', data);
    });

    this.socket.on('chat:messages_read', (data) => {
      this.notify(this.chatMessagesReadListeners, data);
    });

    this.socket.on('chat:claimed', (data) => {
      this.notify(this.chatClaimedListeners, data);
    });

    this.socket.on('chat:unclaimed', (data) => {
      this.notify(this.chatUnclaimedListeners, data);
    });

    this.socket.on('chat:assigned_to_you', (data) => {
      this.notify(this.chatUnclaimedListeners, data);
    });

    this.socket.on('chat:ended', (data) => {
      this.notify(this.chatEndedListeners, data);
    });

    this.socket.on('message_read', (data) => {
      this.emitToListeners('message_read', data);
    });

    this.socket.on('conversation_updated', (data) => {
      this.emitToListeners('conversation_updated', data);
    });

    this.socket.on('chat:typing', (data) => {
      this.notify(this.chatTypingListeners, data);
    });

    this.socket.on('user_typing', (data) => {
      this.emitToListeners('user_typing', data);
    });

    this.socket.on('user_stop_typing', (data) => {
      this.emitToListeners('user_stop_typing', data);
    });
  }

  /**
   * Add event listener for socket events
   * @param {string} event - Event name
   * @param {function} callback - Callback function
   */
  addEventListener(event, callback) {
    if (!this.listeners[event]) {
      this.listeners[event] = [];
    }
    this.listeners[event].push(callback);
  }

  /**
   * Remove event listener
   * @param {string} event - Event name
   * @param {function} callback - Callback function to remove
   */
  removeEventListener(event, callback) {
    if (this.listeners[event]) {
      this.listeners[event] = this.listeners[event].filter(cb => cb !== callback);
    }
  }

  /**
   * Emit event to all registered listeners
   * @param {string} event - Event name
   * @param {*} data - Event data
   */
  emitToListeners(event, data) {
    if (this.listeners[event]) {
      this.notify(this.listeners[event], data);
    }
  }

  notify(callbacks, data) {
    callbacks.forEach(callback => {
      try {
        callback(data);
      } catch (error) {
        console.error('SocketService: Error in listener:', error);
      }
    });
  }

  /**
   * Subscribe to one of the listener arrays. Returns unsubscribe function.
   */
  subscribe(key, callback) {
    if (typeof callback !== 'function') return () => { };
    this[key].push(callback);
    return () => {
      this[key] = this[key].filter(cb => cb !== callback);
    };
  }

  /**
   * Send typing indicator
   * @param {string} conversationId - Conversation ID
   * @param {string} userId - Sender user ID
   */
  sendTyping(conversationId, userId) {
    if (this.socket && this.isConnected) {
      this.socket.emit('chat:typing', { conversation_id: conversationId, is_typing: true, user_id: userId });
    }
  }

  /**
   * Send stop typing indicator
   * @param {string} conversationId - Conversation ID
   * @param {string} userId - Sender user ID
   */
  sendStopTyping(conversationId, userId) {
    if (this.socket && this.isConnected) {
      this.socket.emit('chat:typing', { conversation_id: conversationId, is_typing: false, user_id: userId });
    }
  }

  /**
   * Disconnect socket
   */
  disconnect() {
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
      this.activeConversationId = null;
      console.log('🔌 Socket disconnected');
    }
  }

  /**
   * Get connection status
   */
  getConnectionStatus() {
    return this.isConnected;
  }

  // Listener registration (match React Native callService API). Each returns an unsubscribe function.
  addChatMessageListener(callback) { return this.subscribe('chatMessageListeners', callback); }
  addChatTypingListener(callback) { return this.subscribe('chatTypingListeners', callback); }
  addChatClaimedListener(callback) { return this.subscribe('chatClaimedListeners', callback); }
  addChatUnclaimedListener(callback) { return this.subscribe('chatUnclaimedListeners', callback); }
  addChatEndedListener(callback) { return this.subscribe('chatEndedListeners', callback); }
  addChatMessagesReadListener(callback) { return this.subscribe('chatMessagesReadListeners', callback); }

  /**
   * Add callback to execute when socket connects
   */
  onConnected(callback) {
    if (typeof callback === 'function') {
      this.onConnectedCallbacks.push(callback);
    }
  }

  /**
   * Remove connection callback
   */
  removeOnConnected(callback) {
    this.onConnectedCallbacks = this.onConnectedCallbacks.filter(cb => cb !== callback);
  }

  /**
   * Trigger all connection callbacks
   */
  triggerConnectedCallbacks() {
    this.notify(this.onConnectedCallbacks);
  }
}

// Create singleton instance
const socketService = new SocketService();

export default socketService;
